import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import { format, isValid, parseISO } from "date-fns";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
import { useCookies } from "react-cookie";
// hooks
import { useTransportLanguageRefresh } from "../../../common/hooks/useTransportLanguageRefresh";
// components
import AppDatePickerCustom from "../../../components/AppDatePickerCustom";
import { TransportRowAction, TransportRowAction2Buttons } from "../shared/transportRow";
import ObbPassengersForm from "./ObbPassengersForm";
import ObbSummary from "./ObbSummary";
import AppBookingError from "../../../components/AppBookingError";
import PaymentModal from "../../../components/Payment/PaymentModal";
import AppTimeComponent from "../../../components/AppTimeComponent";
import AppCustomRouteAsync from "../../../components/AppCustomRouteAsync";
import AppButton from "../../../components/ui/AppButton";
import LastMileInfo from "../../../components/LastMileInfo";
import TransportProviderHeader from "../../../components/TransportProviderContent/TransportProviderHeader";
// helpers
import { checkForEmptyNames } from "./helpers/checkForEmptyNames";
import { getAirlineObbPrefillData } from "./helpers/getAirlineObbPrefillData";
import { mapAirlineStationToRouteOption } from "./helpers/mapAirlineStationToRouteOption";
import { resolveObbStationCoords } from "./helpers/resolveObbStationCoords";
import { parseAirlineDepartureTime } from "../../../common/utils/helpers/parseAirlineDepartureTime";
import { hoursUpdate } from "../../../components/AppTimeComponent/helpers/updateHours";
import { updateMinutes } from "../../../components/AppTimeComponent/helpers/updateMinutes";
import { checkIsItInFuture } from "../../../common/utils/helpers/checkIsItInFuture";
import { navigateBackOrToMain } from "../../../common/utils/helpers/navigateBackOrToMain";
import {
  buildAirlineNavigationState,
  clearPersistedAirlineRouterState,
  persistAirlineFromNavigationState,
  resolveTransportAirlineApiContext
} from "../../../common/utils/transport/airlineTransportContext";
import { pushTransportBookMarketplaceContext } from "../../../common/utils/analytics/pushTransportBookMarketplaceContext";
// actions
import { useActions } from "../../../common/hooks/useActions";
import { obbActionCreators } from "./obbModule";
import { selectApiBaseUrl } from "../../../common/store/modules/globalModule";
// icons
import { dateIcon, serviceClassIcon, timeIcon, directionIcon } from "../../../common/vector";

type OptionType = { value: string; label: string; stationCode: string };

const outwardDirection = "OUTWARD";
const roundDirection = "ROUND";

export default function Obb() {
  const theme = useTheme();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cookies] = useCookies(["cookieAccepted"]);
  const language = localStorage.getItem("i18nextLng") || "en";
  const [searchParams, setSearchParams] = useSearchParams();
  const fromParam: string | null = searchParams.get("from");
  const toParam: string | null = searchParams.get("to");

  useLayoutEffect(() => {
    persistAirlineFromNavigationState(location.state);
  }, [location.state]);

  const restoredNavState = useMemo(() => buildAirlineNavigationState(location.state), [location.state]);

  const airlineSharedRoute = useMemo(() => {
    const nav = (restoredNavState ?? location.state) as
      | {
          meta?: { source?: string };
          shared?: {
            milesAndMore?: string;
            arrivalDestination?: {
              placeId?: string | null;
              formattedAddress?: { secondary_text?: string | null } | null;
            } | null;
            finalDestination?: {
              placeId?: string | null;
              description?: string | null;
            } | null;
          };
        }
      | null
      | undefined;

    if (nav?.meta?.source !== "airline") {
      return {
        milesAndMore: "",
        departureAddress: null as string | null,
        departureAddressId: null as string | null,
        destinationAddress: null as string | null,
        destinationAddressId: null as string | null
      };
    }

    return {
      milesAndMore: nav.shared?.milesAndMore ?? "",
      departureAddress: nav.shared?.arrivalDestination?.formattedAddress?.secondary_text ?? null,
      departureAddressId: nav.shared?.arrivalDestination?.placeId ?? null,
      destinationAddress: nav.shared?.finalDestination?.description ?? null,
      destinationAddressId: nav.shared?.finalDestination?.placeId ?? null
    };
  }, [location.state, restoredNavState]);

  const airlinePrefillData = useMemo(
    () => getAirlineObbPrefillData(restoredNavState ?? location.state),
    [location.state, restoredNavState]
  );

  const obbTransportApiContext = useMemo(() => resolveTransportAirlineApiContext(location.state), [location.state]);
  const isAirlineSource = Boolean(airlinePrefillData);
  const airlineDeparture = airlinePrefillData?.departure || null;
  const airlineDestination = airlinePrefillData?.destination || null;
  const airlineDepartureDate = airlinePrefillData?.departureDate || null;
  const airlineDepartureTime = airlinePrefillData?.departureTime || null;

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [serviceClass, setServiceClass] = useState(2);
  const [direction, setDirection] = useState(outwardDirection);
  const [obbHours, setObbHours] = useState(new Date().getHours());
  const [obbMinutes, setObbMinutes] = useState(new Date().getMinutes());
  const [selectedFromStation, setSelectedFromStation] = useState<OptionType | null>(null);
  const [selectedToStation, setSelectedToStation] = useState<OptionType | null>(null);
  const [disableCustomRouteAsync, setDisableCustomRouteAsync] = useState(false);
  const [selectedToStationCoords, setSelectedToStationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const skipAirlineRoutePrefillRef = useRef(false);

  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const obbBookingResponseData = useSelector((state: any) => state.obb.obbBookingResponseData);
  const obbCustomRouteCode = useSelector((state: any) => state.obb.obbCustomRouteCode);
  const obbInfoError = useSelector((state: any) => state.obb.obbInfoError);
  const obbData = useSelector((state: any) => state.obb.obbData);
  const selectedStationFrom = useSelector((state: any) => state.obb.selectedStationFrom);
  const selectedStationTo = useSelector((state: any) => state.obb.selectedStationTo);
  const passengerList = useSelector((state: any) => state.obb.passengerList);
  const errorPrebook = useSelector((state: any) => state.obb.errorPrebook);
  const prebookPriceInfo = useSelector((state: any) => state.obb.prebookPriceInfo);
  const obbBookingError = useSelector((state: any) => state.obb.obbBookingError);
  const showObbPaymentModal = useSelector((state: any) => state.obb.showObbPaymentModal);
  const globalLoader = useSelector((state: any) => state.global.globalLoader);
  const apiUrl = useSelector(selectApiBaseUrl);
  const lastMileRoute = useSelector((state: any) => state.obb.lastMileRoute);
  const isLastMileRouteLoading = useSelector((state: any) => state.obb.isLastMileRouteLoading);
  const lastMileRouteError = useSelector((state: any) => state.obb.lastMileRouteError);

  const getObbInfo = useActions(obbActionCreators?.getObbInfoAction, []);
  const searchObbStations = useActions(obbActionCreators?.searchObbStationsAction, []);
  const getPriceObbPrebook = useActions(obbActionCreators?.getPriceObbPrebookAction, []);
  const buyObbTicket = useActions(obbActionCreators?.buyObbTicketAction, []);
  const resetObbState = useActions(obbActionCreators?.resetObbStateAction, []);
  const toggleObbPaymentModal = useActions(obbActionCreators?.toggleObbPaymentModalAction, []);
  const toggleObbBookingError = useActions(obbActionCreators?.toggleObbBookingErrorAction, []);
  const getObbLastMileRoute = useActions(obbActionCreators?.getObbLastMileRouteAction, []);

  const navigateObb = useCallback(
    (to: string, options?: { replace?: boolean }) => {
      navigate(to, { replace: options?.replace, state: buildAirlineNavigationState(location.state) });
    },
    [navigate, location.state]
  );

  useEffect(() => {
    updateMinutes(setObbMinutes, setObbHours, "initial", 20, obbHours, obbMinutes);

    return () => {
      resetObbState();
      clearPersistedAirlineRouterState();
    };
  }, []);

  useEffect(() => {
    if (tenantInfo) {
      getObbInfo(obbTransportApiContext);
    }
  }, [tenantInfo, obbTransportApiContext]);

  useEffect(() => {
    if (!airlineDepartureDate) {
      return;
    }

    const parsedDate = parseISO(airlineDepartureDate);
    if (!isValid(parsedDate)) {
      return;
    }

    setSelectedDate(parsedDate);
  }, [airlineDepartureDate]);

  useEffect(() => {
    const parsedTime = parseAirlineDepartureTime(airlineDepartureTime);
    if (!parsedTime) {
      return;
    }

    setObbHours(parsedTime.hours);
    setObbMinutes(parsedTime.minutes);
  }, [airlineDepartureTime]);

  useEffect(() => {
    if (
      skipAirlineRoutePrefillRef.current ||
      !isAirlineSource ||
      !airlineDeparture?.name ||
      !airlineDestination?.name ||
      fromParam ||
      toParam
    ) {
      return;
    }

    setSearchParams(
      { from: airlineDeparture.name, to: airlineDestination.name },
      { replace: true, state: buildAirlineNavigationState(location.state) }
    );
  }, [isAirlineSource, airlineDeparture, airlineDestination, fromParam, toParam, location.state, setSearchParams]);

  useEffect(() => {
    if (
      skipAirlineRoutePrefillRef.current ||
      !isAirlineSource ||
      !airlineDeparture?.name ||
      !airlineDestination?.name
    ) {
      return;
    }

    const departureStation = mapAirlineStationToRouteOption(airlineDeparture);
    const destinationStation = mapAirlineStationToRouteOption(airlineDestination);
    if (!departureStation || !destinationStation) {
      return;
    }

    setSelectedFromStation(departureStation);
    setSelectedToStation(destinationStation);
    const destinationCoords = resolveObbStationCoords(airlineDestination);
    if (destinationCoords) {
      setSelectedToStationCoords(destinationCoords);
    }
  }, [isAirlineSource, airlineDeparture, airlineDestination]);

  // --- LAST MILE ---
  const isAirlineTransferHub = obbTransportApiContext?.mode === "airline";
  const destinationPlaceId = airlineSharedRoute?.destinationAddressId || null;
  const destinationName = airlineSharedRoute?.destinationAddress || "";

  useEffect(() => {
    if (
      !isAirlineTransferHub ||
      !destinationPlaceId ||
      typeof selectedToStationCoords?.lat !== "number" ||
      typeof selectedToStationCoords?.lng !== "number"
    ) {
      return;
    }

    getObbLastMileRoute({
      departureLatitude: selectedToStationCoords.lat,
      departureLongitude: selectedToStationCoords.lng,
      destinationPlaceId,
      travelMode: "walking"
    });
  }, [isAirlineTransferHub, destinationPlaceId, selectedToStationCoords, getObbLastMileRoute]);
  // --- END OF LAST MILE ---

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: "/transport/obb",
    getNavigationState: () => buildAirlineNavigationState(locationRef.current.state),
    iataCode: obbTransportApiContext.mode === "airline" ? obbTransportApiContext.iataCode : null
  });

  useEffect(() => {
    if (fromParam && fromParam !== selectedFromStation?.value) {
      searchObbStations(fromParam, "from", obbTransportApiContext);
    }
    if (toParam && toParam !== selectedToStation?.value) {
      searchObbStations(toParam, "to", obbTransportApiContext);
    }
  }, [
    apiUrl,
    fromParam,
    toParam,
    selectedFromStation?.value,
    selectedToStation?.value,
    searchObbStations,
    obbTransportApiContext
  ]);

  const onObbStationInputChange = useCallback(
    (keyword: string) => searchObbStations(keyword, "", obbTransportApiContext),
    [searchObbStations, obbTransportApiContext]
  );

  useEffect(() => {
    if (selectedStationFrom?.[0]) {
      onChangeFromAutoComplete(selectedStationFrom?.[0]);
    }
  }, [selectedStationFrom]);

  useEffect(() => {
    if (selectedStationTo?.[0]) {
      onChangeToAutoComplete(selectedStationTo?.[0]);
    }
  }, [selectedStationTo]);

  const onChangeFromAutoComplete = (station: any) => {
    if (isAirlineSource) {
      skipAirlineRoutePrefillRef.current = true;
    }

    if (station?.name) {
      setSelectedFromStation({
        value: station?.name,
        label: station?.name,
        stationCode: station?.station_code != null ? String(station.station_code) : ""
      });

      if (toParam) {
        navigateObb(`/${language}/transport/obb?from=${station?.name}&to=${toParam}`, { replace: true });
      } else {
        navigateObb(`/${language}/transport/obb?from=${station?.name}`, { replace: true });
      }
    } else {
      setSelectedFromStation(null);

      if (toParam) {
        navigateObb(`/${language}/transport/obb?to=${toParam}`, { replace: true });
      } else {
        navigateObb(`/${language}/transport/obb`, { replace: true });
      }
    }
  };

  const onChangeToAutoComplete = (station: any) => {
    if (isAirlineSource) {
      skipAirlineRoutePrefillRef.current = true;
    }

    if (station?.name) {
      setSelectedToStation({
        value: station?.name,
        label: station?.name,
        stationCode: station?.station_code != null ? String(station.station_code) : ""
      });
      setSelectedToStationCoords(resolveObbStationCoords(station));

      if (fromParam) {
        navigateObb(`/${language}/transport/obb?from=${fromParam}&to=${station?.name}`, { replace: true });
      } else {
        navigateObb(`/${language}/transport/obb?to=${station?.name}`, { replace: true });
      }
    } else {
      setSelectedToStation(null);
      setSelectedToStationCoords(null);

      if (fromParam) {
        navigateObb(`/${language}/transport/obb?from=${fromParam}`, { replace: true });
      } else {
        navigateObb(`/${language}/transport/obb`, { replace: true });
      }
    }
  };

  const handleSeePriceBtn = () => {
    const startDate: Date = new Date(selectedDate);
    startDate.setHours(obbHours);
    startDate.setMinutes(obbMinutes);

    getPriceObbPrebook(
      {
        qualityOfService: serviceClass,
        direction,
        start_date: format(startDate, "yyyy-MM-dd HH:mm"),
        offer_code: obbCustomRouteCode,
        milesAndMore: airlineSharedRoute.milesAndMore,
        ...(isAirlineSource
          ? {
              departureAddress: airlineSharedRoute.departureAddress,
              departureAddressId: airlineSharedRoute.departureAddressId,
              destinationAddress: airlineSharedRoute.destinationAddress,
              destinationAddressId: airlineSharedRoute.destinationAddressId
            }
          : {}),
        origin: selectedFromStation
          ? {
              name: selectedFromStation?.value,
              station_code: selectedFromStation?.stationCode
            }
          : null,
        destination: selectedToStation
          ? {
              name: selectedToStation?.value,
              station_code: selectedToStation?.stationCode
            }
          : null
      },
      obbTransportApiContext
    );
  };

  const handleBuyTicketBtn = () => {
    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: obbCustomRouteCode ?? null
    });

    buyObbTicket(
      {
        start_date: Math.round(+new Date(selectedDate) / 1000),
        timeslot: `${obbHours}:${obbMinutes}`,
        offer_code: obbCustomRouteCode
      },
      navigate,
      obbTransportApiContext
    );
  };

  const handleValidationSeePriceBtn = (): boolean => {
    const isSelectedRoute: boolean = Boolean(selectedFromStation && selectedToStation);
    return (
      !isSelectedRoute || !checkIsItInFuture(selectedDate, obbHours, obbMinutes, 0) || checkForEmptyNames(passengerList)
    );
  };

  const renderPrebookErrorMsg = () => {
    if (checkIsItInFuture(selectedDate, obbHours, obbMinutes, 0) && !errorPrebook) {
      return null;
    }
    let errors: Array<any> = [];
    if (!checkIsItInFuture(selectedDate, obbHours, obbMinutes, 0)) {
      errors.push({
        errorMessage: t("transport.timeErrorMsg")
      });
    }
    if (errorPrebook?.fields) {
      errors = errors?.concat(errorPrebook?.fields);
    }

    if (!errors?.length) {
      errors.push({
        errorMessage: t("transport.prebookCustomError")
      });
    }

    return errors?.map((error: any, i: number) => (
      <div key={i} className="obb__error-msg">
        {error?.field && `${error?.field} -`} {error?.errorMessage}
      </div>
    ));
  };

  const getDisabledStyleForTicketsForm = () => {
    if (prebookPriceInfo) {
      return { opacity: 0.4, pointerEvents: "none" };
    }
  };

  return (
    <Grid
      className="obb grid-box"
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      {obbBookingError ? (
        <AppBookingError
          dataAttribute="obb-booking-error-button"
          onButtonClick={() => toggleObbBookingError(false)}
          title={t("bookingForm.error.title")}
          subtitle={t("bookingForm.error.message")}
        />
      ) : (
        <>
          <TransportProviderHeader
            title={obbData?.provider_title ?? obbData?.title}
            shortDescription={obbData?.provider_description}
            providerLogo={obbData?.provider_logo}
            titleClassName="obb__title"
          />

          {obbCustomRouteCode && !obbInfoError ? (
            <Grid container>
              <Grid
                item
                sx={{ display: "flex", flexDirection: "column", ...getDisabledStyleForTicketsForm() }}
                md={6}
                xs={12}
              >
                <TransportRowAction
                  label={t("transport.departureDate")}
                  icon={dateIcon?.default}
                  actionComponent={
                    <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                      <AppDatePickerCustom
                        selectedDate={selectedDate}
                        setSelectedDate={date => date != null && setSelectedDate(date)}
                        minDate={new Date()}
                      />
                    </Grid>
                  }
                />
                <TransportRowAction
                  label={t("transport.departureTime")}
                  icon={timeIcon?.default}
                  actionComponent={
                    <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                      <AppTimeComponent
                        hours={obbHours}
                        minutes={obbMinutes}
                        handleHoursChange={(change: string, amount: number) =>
                          hoursUpdate(setObbHours, change, amount, obbHours)
                        }
                        handleMinutesChange={(change: string, amount: number) =>
                          updateMinutes(setObbMinutes, setObbHours, change, amount, obbHours, obbMinutes)
                        }
                        timeBoxSize="w-80"
                      />
                    </Grid>
                  }
                />
                <TransportRowAction
                  label={t("transport.serviceClass")}
                  icon={serviceClassIcon?.default}
                  actionComponent={
                    <TransportRowAction2Buttons
                      firstButtonLabel={"1"}
                      firstButtonHandle={() => setServiceClass(1)}
                      secondButtonLabel={"2"}
                      secondButtonHandle={() => setServiceClass(2)}
                      isFirstButtonActive={serviceClass === 1}
                    />
                  }
                />
                <TransportRowAction
                  label={t("transport.direction")}
                  icon={directionIcon?.default}
                  actionComponent={
                    <TransportRowAction2Buttons
                      firstButtonLabel={t("transport.oneWayBtn")}
                      firstButtonHandle={() => setDirection(outwardDirection)}
                      secondButtonLabel={t("transport.returnBtn")}
                      secondButtonHandle={() => setDirection(roundDirection)}
                      isFirstButtonActive={direction === outwardDirection}
                      fontSize="11px"
                    />
                  }
                />
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
                mt={{ sm: 6, xs: 6, md: 0 }}
                sx={{ ...getDisabledStyleForTicketsForm() }}
                position={"relative"}
              >
                <div className="vertical-divider" />
                <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }}>
                  <AppCustomRouteAsync
                    setDisableCustomRouteAsync={setDisableCustomRouteAsync}
                    disableCustomRouteAsync={disableCustomRouteAsync}
                    selectedToStation={selectedToStation}
                    selectedFromStation={selectedFromStation}
                    onInputChange={onObbStationInputChange}
                    onFocusCustomRouteAsync={() => undefined}
                    firstInputId="search-obb-from-input"
                    secondInputId="search-obb-to-input"
                    setSelectedFromStation={onChangeFromAutoComplete}
                    setSelectedToStation={onChangeToAutoComplete}
                  />
                  {isAirlineTransferHub && destinationName ? (
                    <LastMileInfo
                      title={t("transport.lastMile.title")}
                      icon={<DirectionsWalkIcon fontSize="small" />}
                      destinationName={destinationName}
                      distanceMeters={lastMileRoute?.distance_meters ?? null}
                      durationSeconds={lastMileRoute?.duration_seconds ?? null}
                      googleMapsUrl={lastMileRoute?.google_maps_url ?? null}
                      isLoading={isLastMileRouteLoading}
                      error={lastMileRouteError}
                    />
                  ) : null}
                  <ObbPassengersForm />
                  {renderPrebookErrorMsg()}
                  <Grid
                    container={isAirlineSource}
                    item={!isAirlineSource}
                    xs={12}
                    sm={isAirlineSource ? undefined : 4}
                    sx={{
                      ...(isAirlineSource ? { display: "flex", alignItems: "stretch", gap: 2 } : {})
                    }}
                  >
                    {isAirlineSource && (
                      <Grid item sx={{ flex: 1 }}>
                        <AppButton
                          label={t("transport.goBack")}
                          onClick={() => navigateBackOrToMain(navigate, { language })}
                          disableRipple={true}
                          customBackgroundColor={theme.palette.primary.dark}
                          customHoverColor={theme.palette.primary.dark}
                          fontSize="14px"
                          dataAttribute="obb-go-back-1"
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      sx={{
                        opacity: handleValidationSeePriceBtn() ? 0.8 : 1,
                        ...(isAirlineSource ? { flex: 1 } : {})
                      }}
                    >
                      <AppButton
                        label={t("transport.seePriceBtn")}
                        onClick={handleSeePriceBtn}
                        disableRipple={true}
                        customBackgroundColor={tenantInfo?.style?.button?.color}
                        customActiveColor={tenantInfo?.style?.button?.active}
                        customHoverColor={tenantInfo?.style?.button?.hover}
                        customClickColor={tenantInfo?.style?.button?.click}
                        fontSize="14px"
                        disabled={handleValidationSeePriceBtn()}
                        dataAttribute="obb-see-price"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} mt={{ xs: 4, sm: 6 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
                {prebookPriceInfo && (
                  <ObbSummary onBuyTicket={handleBuyTicketBtn} cancellationPolicy={obbData?.cancellation_policy} />
                )}
              </Grid>
            </Grid>
          ) : (
            !globalLoader && (
              <Grid item xs={12} pb={4}>
                <Box sx={{ fontSize: 18 }}>{t("transport.notAvailable")}</Box>
              </Grid>
            )
          )}
        </>
      )}
      {showObbPaymentModal && (
        <PaymentModal
          isOpen={showObbPaymentModal}
          bookingResponseData={obbBookingResponseData}
          togglePaymentModal={toggleObbPaymentModal}
          redirectUrl="transport/obb/confirmation"
        />
      )}
    </Grid>
  );
}
